/**
 * Synthetic Monitor Service
 *
 * All synthetic monitor operations use the Scraper API (scrapperApi / NEXT_PUBLIC_SCRAPPER_API_BASE_URL):
 * - List: GET /synthetic_monitor/monitors
 * - Detail: GET /synthetic_monitor/monitors/:id
 * - Create: POST /synthetic_monitor/monitors
 * - Update: PUT /synthetic_monitor/monitors/:id
 * - Delete: DELETE /synthetic_monitor/monitors/:id
 * - Test execution: apiRoutes.syntheticMonitorTest (login, PLP, PDP, cart, checkout).
 *
 * UI → Backend mapping (see documents/features/synthetic-monitor-api-mapping.md):
 * - Login  → one API per checkbox: test_login_successful, test_login_invalid_credentials, test_login_nonexistent_email, test_login_empty_fields
 * - PLP    → one API per checkbox: test_plp_page_load, test_plp_page_grid, test_plp_product_availability, test_plp_add_to_cart
 * - PDP    → one API per checkbox: test_pdp_basic_content, test_pdp_product_details, test_pdp_image_gallery, test_pdp_add_to_cart
 * - Cart → one API per checkbox: test_cart_view_products, test_cart_update_quantity, test_cart_remove_product, test_cart_proceed_checkout
 * - Checkout → one API per checkbox: test_checkout_access, test_checkout_shipping_address, test_checkout_order_summary, test_checkout_payment_method, test_checkout_place_order, test_checkout_order_confirmation
 */

import { apiRoutes } from '@/config/routes';
import api, { scrapperApi, ApiError } from './api';

// ---------------------------------------------------------------------------
// Types (align with backend when APIs are available)
// ---------------------------------------------------------------------------

/** Scenario selections: scenarioId -> optionId -> checked */
export type ScenarioCheckedOptions = Record<string, Record<string, boolean>>;

/** Page URL per scenario: scenarioId -> url */
export type ScenarioPageUrls = Record<string, string>;

/** Payload for creating a synthetic monitor (website details + schedule + scenario config) */
export interface CreateSyntheticMonitorPayload {
  name: string;
  url: string;
  login?: string;
  password?: string;
  schedule_type: string;
  schedule_frequency: number;
  timezone: string;
  active_hours_start?: string;
  active_hours_end?: string;
  schedule_frequency_timings?: string[];
  /** Scenario selections, page URLs, and optional checkout shipping address */
  scenarios?: {
    checked_options: ScenarioCheckedOptions;
    page_urls: ScenarioPageUrls;
    shipping_address?: string;
  };
  created_by?: string;
}

/** Payload for updating a synthetic monitor (same shape as create, or partial) */
export interface UpdateSyntheticMonitorPayload extends Partial<CreateSyntheticMonitorPayload> {
  id?: number;
}

/** Response from create/update and GET detail (align with backend). List/detail may return monitor_name, website_url, created_on. */
export interface SyntheticMonitorResponse {
  id: number | string;
  name?: string;
  url?: string;
  /** Backend list/detail often returns monitor_name (site name). */
  monitor_name?: string;
  /** Backend may return site (display name or site identifier). */
  site?: string;
  /** Backend may return created_on, create, or created (creation timestamp); also support created_at. */
  created_on?: string;
  create?: string;
  created?: string;
  created_at?: string;
  updated_at?: string;
  /** Backend list/detail often returns website_url. */
  website_url?: string;
  [key: string]: unknown;
}

/** Single synthetic monitor (for get/list) */
export interface SyntheticMonitor extends SyntheticMonitorResponse {}

/** Payload for create_flow: complete flow from Login to Checkout (single POST). Backend expects username/password (not login_username/login_password). */
export interface CreateSyntheticMonitorFlowPayload {
  monitor_name: string;
  website_url: string;
  login_url: string;
  username: string;
  password: string;
  plp_url: string;
  pdp_url: string;
  cart_url: string;
  checkout_url: string;
  device_type?: string;
  timeout?: number;
  scheduling_mode?: string;
  timezone?: string;
  enable_login: boolean;
  enable_plp: boolean;
  enable_pdp: boolean;
  enable_cart: boolean;
  enable_checkout: boolean;
}

/** Response from create_flow (monitor_id and list of scenarios created). */
export interface CreateSyntheticMonitorFlowResponse {
  monitor_id: number;
  scenarios?: unknown[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// API calls (will work once backend exposes these endpoints)
// ---------------------------------------------------------------------------

/**
 * Create a new synthetic monitor.
 * Uses POST /synthetic_monitor/monitors via Scraper API (same as list, detail, tests).
 */
export async function createSyntheticMonitor(
  payload: CreateSyntheticMonitorPayload
): Promise<SyntheticMonitor> {
  try {
    const response = await scrapperApi.post<SyntheticMonitor | SyntheticMonitorResponse>(
      apiRoutes.syntheticMonitorTest.monitorCreate,
      payload
    );
    return response as SyntheticMonitor;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Failed to create synthetic monitor'
    );
  }
}

/**
 * Create a complete synthetic monitoring flow (Login → PLP → PDP → Cart → Checkout).
 * Uses POST /synthetic_monitor/monitors/create_flow via Scraper API.
 */
export async function createSyntheticMonitorFlow(
  payload: CreateSyntheticMonitorFlowPayload
): Promise<CreateSyntheticMonitorFlowResponse> {
  try {
    const response = await scrapperApi.post<CreateSyntheticMonitorFlowResponse>(
      apiRoutes.syntheticMonitorTest.monitorCreateFlow,
      payload
    );
    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Failed to create synthetic monitor flow'
    );
  }
}

/**
 * Update an existing synthetic monitor.
 * Uses PUT /synthetic_monitor/monitors/:id via Scraper API (same as list, detail, create, tests).
 */
export async function updateSyntheticMonitor(
  id: number,
  payload: UpdateSyntheticMonitorPayload
): Promise<SyntheticMonitor> {
  try {
    const response = await scrapperApi.put<SyntheticMonitor | SyntheticMonitorResponse>(
      apiRoutes.syntheticMonitorTest.monitorUpdate(id),
      payload
    );
    return response as SyntheticMonitor;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Failed to update synthetic monitor'
    );
  }
}

/**
 * Delete a synthetic monitor.
 * Uses DELETE /synthetic_monitor/monitors/:id via Scraper API.
 */
export async function deleteSyntheticMonitor(id: number): Promise<void> {
  await scrapperApi.delete(apiRoutes.syntheticMonitorTest.monitorDelete(id));
}

/**
 * Fetch a single synthetic monitor by id.
 * Uses GET /synthetic_monitor/monitors/:id via Scraper API (same as list and test endpoints).
 * Id can be numeric or UUID string.
 * Handles wrapped responses: { data: {...} } or { monitor: {...} }.
 */
export async function getSyntheticMonitorById(id: number | string): Promise<SyntheticMonitor | null> {
  try {
    const response = await scrapperApi.get<unknown>(
      apiRoutes.syntheticMonitorTest.monitorDetail(id)
    );
    if (response == null) return null;
    if (typeof response === 'object' && !Array.isArray(response)) {
      const obj = response as Record<string, unknown>;
      const unwrapped = obj.monitor ?? obj.data;
      if (unwrapped != null && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
        return unwrapped as SyntheticMonitor;
      }
    }
    return response as SyntheticMonitor;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** Sort option for list synthetic monitors API. */
export type ListSyntheticMonitorsSort = 'created_desc' | 'created_asc' | 'name_asc' | 'name_desc';

/** Params for list synthetic monitors (Scraper API: GET /synthetic_monitor/monitors). */
export interface ListSyntheticMonitorsParams {
  /** Search by monitor_name or website_url (partial match). */
  search?: string;
  /** Filter by scenario group (e.g. login, pdp, plp, checkout). */
  scenario_group?: string;
  /** Sort: created_desc, created_asc, name_asc, name_desc. */
  sort?: ListSyntheticMonitorsSort;
}

/**
 * List synthetic monitors.
 * Uses GET /synthetic_monitor/monitors via Scraper API (search, scenario_group, sort).
 */
export async function listSyntheticMonitors(
  params?: ListSyntheticMonitorsParams
): Promise<SyntheticMonitor[]> {
  try {
    const query: Record<string, string> = {
      sort: params?.sort ?? 'created_desc',
    };
    if (params?.search != null && params.search !== '') {
      query.search = params.search;
    }
    if (params?.scenario_group != null && params.scenario_group !== '') {
      query.scenario_group = params.scenario_group;
    }
    const response = await scrapperApi.get<unknown>(
      apiRoutes.syntheticMonitorTest.monitorsList,
      query
    );
    if (Array.isArray(response)) {
      return response as SyntheticMonitor[];
    }
    if (response && typeof response === 'object') {
      const obj = response as Record<string, unknown>;
      for (const key of ['data', 'monitors', 'results', 'items']) {
        if (Array.isArray(obj[key])) {
          return obj[key] as SyntheticMonitor[];
        }
      }
    }
    return [];
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return [];
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Synthetic Test Execution (GlassX Scraper API – maps to UI scenarios)
// ---------------------------------------------------------------------------

/** Health check response for synthetic monitor service. */
export interface SyntheticMonitorHealthResponse {
  status?: string;
  message?: string;
  [key: string]: unknown;
}

/**
 * Check synthetic monitor service health.
 * Uses GET /synthetic_monitor/health via Scraper API.
 */
export async function getSyntheticMonitorHealth(): Promise<SyntheticMonitorHealthResponse> {
  try {
    const response = await scrapperApi.get<SyntheticMonitorHealthResponse>(
      apiRoutes.syntheticMonitorTest.health
    );
    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Synthetic monitor service health check failed'
    );
  }
}

/** Request for test_login_* (Login scenario). Same body for all 4 variants (successful, invalid_credentials, nonexistent_email, empty_fields). */
export interface TestLoginRequest {
  website_url: string;
  login_url: string;
  username: string;
  password: string;
  monitor_name?: string;
  device_type?: 'desktop' | 'mobile' | 'tablet';
  timeout?: number;
}

/** Login option IDs in the UI (checkboxes) map to these API variants. */
export type LoginTestVariant = 'successful' | 'invalid_credentials' | 'nonexistent_email' | 'empty_fields';

const LOGIN_VARIANT_TO_PATH: Record<LoginTestVariant, string> = {
  successful: apiRoutes.syntheticMonitorTest.testLoginSuccessful,
  invalid_credentials: apiRoutes.syntheticMonitorTest.testLoginInvalidCredentials,
  nonexistent_email: apiRoutes.syntheticMonitorTest.testLoginNonexistentEmail,
  empty_fields: apiRoutes.syntheticMonitorTest.testLoginEmptyFields,
};

/** Request for test_plp_* (PLP scenario). Same body for all 4 variants (page_load, page_grid, product_availability, add_to_cart). */
export interface TestPlpRequest {
  page_url: string;
  monitor_name?: string;
  expected_text?: string;
  device_type?: 'desktop' | 'mobile' | 'tablet';
  performance_threshold_ms?: number;
  timeout?: number;
  session_cookies?: unknown;
  login_first?: {
    website_url: string;
    login_url: string;
    username: string;
    password: string;
  };
}

/** PLP option IDs in the UI (checkboxes) map to these API variants. */
export type PlpTestVariant = 'page_load' | 'page_grid' | 'product_availability' | 'add_to_cart';

const PLP_VARIANT_TO_PATH: Record<PlpTestVariant, string> = {
  page_load: apiRoutes.syntheticMonitorTest.testPlpPageLoad,
  page_grid: apiRoutes.syntheticMonitorTest.testPlpPageGrid,
  product_availability: apiRoutes.syntheticMonitorTest.testPlpProductAvailability,
  add_to_cart: apiRoutes.syntheticMonitorTest.testPlpAddToCart,
};

/** Request for test_pdp_* (PDP scenario). Same body for all 4 variants (basic_content, product_details, image_gallery, add_to_cart). */
export interface TestPdpRequest {
  page_url: string;
  monitor_name?: string;
  device_type?: 'desktop' | 'mobile' | 'tablet';
  timeout?: number;
  session_cookies?: unknown;
  login_first?: {
    website_url: string;
    login_url: string;
    username: string;
    password: string;
  };
}

/** PDP option IDs in the UI (checkboxes) map to these API variants. */
export type PdpTestVariant = 'basic_content' | 'product_details' | 'image_gallery' | 'add_to_cart';

const PDP_VARIANT_TO_PATH: Record<PdpTestVariant, string> = {
  basic_content: apiRoutes.syntheticMonitorTest.testPdpBasicContent,
  product_details: apiRoutes.syntheticMonitorTest.testPdpProductDetails,
  image_gallery: apiRoutes.syntheticMonitorTest.testPdpImageGallery,
  add_to_cart: apiRoutes.syntheticMonitorTest.testPdpAddToCart,
};

/** Request for test_cart_* (Cart scenario). Same body for all 4 variants (view_products, update_quantity, remove_product, proceed_checkout). */
export interface TestCartRequest {
  page_url: string;
  monitor_name?: string;
  device_type?: 'desktop' | 'mobile' | 'tablet';
  timeout?: number;
}

/** Cart option IDs in the UI (checkboxes) map to these API variants. */
export type CartTestVariant = 'view_products' | 'update_quantity' | 'remove_product' | 'proceed_checkout';

const CART_VARIANT_TO_PATH: Record<CartTestVariant, string> = {
  view_products: apiRoutes.syntheticMonitorTest.testCartViewProducts,
  update_quantity: apiRoutes.syntheticMonitorTest.testCartUpdateQuantity,
  remove_product: apiRoutes.syntheticMonitorTest.testCartRemoveProduct,
  proceed_checkout: apiRoutes.syntheticMonitorTest.testCartProceedCheckout,
};

/** Request for test_checkout_* (Checkout scenario). Same body for all 6 variants. */
export interface TestCheckoutRequest {
  checkout_url: string;
  monitor_name?: string;
  device_type?: 'desktop' | 'mobile' | 'tablet';
  timeout?: number;
  session_cookies?: unknown[];
}

/** Checkout option IDs in the UI (checkboxes) map to these API variants. */
export type CheckoutTestVariant =
  | 'access'
  | 'shipping_address'
  | 'order_summary'
  | 'payment_method'
  | 'place_order'
  | 'order_confirmation';

const CHECKOUT_VARIANT_TO_PATH: Record<CheckoutTestVariant, string> = {
  access: apiRoutes.syntheticMonitorTest.testCheckoutAccess,
  shipping_address: apiRoutes.syntheticMonitorTest.testCheckoutShippingAddress,
  order_summary: apiRoutes.syntheticMonitorTest.testCheckoutOrderSummary,
  payment_method: apiRoutes.syntheticMonitorTest.testCheckoutPaymentMethod,
  place_order: apiRoutes.syntheticMonitorTest.testCheckoutPlaceOrder,
  order_confirmation: apiRoutes.syntheticMonitorTest.testCheckoutOrderConfirmation,
};

/** Generic test execution response (steps, pass_fail, etc.; shape may vary by endpoint). */
export interface SyntheticTestExecutionResponse {
  scenario?: string;
  workflows?: unknown;
  pass_fail?: boolean;
  execution_time_ms?: number;
  steps?: unknown[];
  logged_in?: boolean;
  timestamp?: string;
  session_cookies?: unknown;
  success?: boolean;
  page_url?: string;
  plp_loaded?: boolean;
  pdp_loaded?: boolean;
  total_duration_ms?: number;
  plp_validations?: unknown;
  pdp_validations?: unknown;
  [key: string]: unknown;
}

/** Client-side timeout for synthetic test API calls (ms). Backend/Playwright can take 60s+; gateway must allow at least this. */
const SYNTHETIC_TEST_REQUEST_TIMEOUT_MS = 180000; // 2 minutes

function fetchWithTimeout(
  fn: (signal: AbortSignal) => Promise<SyntheticTestExecutionResponse>
): Promise<SyntheticTestExecutionResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SYNTHETIC_TEST_REQUEST_TIMEOUT_MS);
  return fn(controller.signal).finally(() => clearTimeout(timeoutId));
}

function buildLoginRequestBody(payload: TestLoginRequest): Record<string, unknown> {
  return {
    website_url: payload.website_url,
    login_url: payload.login_url,
    username: payload.username,
    password: payload.password,
    ...(payload.monitor_name != null && payload.monitor_name !== '' && { monitor_name: payload.monitor_name }),
    device_type: payload.device_type ?? 'desktop',
    timeout: payload.timeout ?? 30000,
  };
}

async function postLoginTestToPath(
  path: string,
  payload: TestLoginRequest
): Promise<SyntheticTestExecutionResponse> {
  try {
    return await fetchWithTimeout((signal) =>
      scrapperApi.post<SyntheticTestExecutionResponse>(
        path,
        buildLoginRequestBody(payload),
        undefined,
        { signal }
      )
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    const isAbort = error instanceof Error && error.name === 'AbortError';
    throw new ApiError(
      isAbort
        ? 'Request timed out. The login test may need more time; try again or ask the server admin to increase the gateway timeout.'
        : error instanceof Error
          ? error.message
          : 'Failed to run synthetic login test'
    );
  }
}

/** Run the given login test variant (each UI checkbox maps to one variant). */
export async function runLoginTestByVariant(
  variant: LoginTestVariant,
  payload: TestLoginRequest
): Promise<SyntheticTestExecutionResponse> {
  return postLoginTestToPath(LOGIN_VARIANT_TO_PATH[variant], payload);
}

/** Run successful-login test. Alias for runLoginTestByVariant('successful', payload). */
export async function testLogin(payload: TestLoginRequest): Promise<SyntheticTestExecutionResponse> {
  return runLoginTestByVariant('successful', payload);
}

function buildPlpRequestBody(payload: TestPlpRequest): Record<string, unknown> {
  return {
    page_url: payload.page_url,
    ...(payload.monitor_name != null && payload.monitor_name !== '' && { monitor_name: payload.monitor_name }),
    expected_text: payload.expected_text,
    device_type: payload.device_type ?? 'desktop',
    performance_threshold_ms: payload.performance_threshold_ms,
    timeout: payload.timeout ?? 30000,
    session_cookies: payload.session_cookies,
    login_first: payload.login_first,
  };
}

async function postPlpTestToPath(
  path: string,
  payload: TestPlpRequest
): Promise<SyntheticTestExecutionResponse> {
  try {
    return await fetchWithTimeout((signal) =>
      scrapperApi.post<SyntheticTestExecutionResponse>(
        path,
        buildPlpRequestBody(payload),
        undefined,
        { signal }
      )
    );
  } catch (error) {
    // If variant endpoint returns 404 (e.g. backend only has legacy test_plp), fall back to single test_plp
    if (error instanceof ApiError && error.status === 404) {
      try {
        return await fetchWithTimeout((signal) =>
          scrapperApi.post<SyntheticTestExecutionResponse>(
            apiRoutes.syntheticMonitorTest.testPlp,
            buildPlpRequestBody(payload),
            undefined,
            { signal }
          )
        );
      } catch (fallbackError) {
        if (fallbackError instanceof ApiError) throw fallbackError;
        throw new ApiError(
          fallbackError instanceof Error ? fallbackError.message : 'Failed to run synthetic PLP test'
        );
      }
    }
    if (error instanceof ApiError) throw error;
    const isAbort = error instanceof Error && error.name === 'AbortError';
    throw new ApiError(
      isAbort
        ? 'Request timed out. Try again or ask the server admin to increase the gateway timeout.'
        : error instanceof Error
          ? error.message
          : 'Failed to run synthetic PLP test'
    );
  }
}

/** Run the given PLP test variant (each UI checkbox maps to one variant). */
export async function runPlpTestByVariant(
  variant: PlpTestVariant,
  payload: TestPlpRequest
): Promise<SyntheticTestExecutionResponse> {
  return postPlpTestToPath(PLP_VARIANT_TO_PATH[variant], payload);
}

/** Run PLP page load test. Alias for runPlpTestByVariant('page_load', payload). */
export async function testPlp(payload: TestPlpRequest): Promise<SyntheticTestExecutionResponse> {
  return runPlpTestByVariant('page_load', payload);
}

function buildPdpRequestBody(payload: TestPdpRequest): Record<string, unknown> {
  return {
    page_url: payload.page_url,
    ...(payload.monitor_name != null && payload.monitor_name !== '' && { monitor_name: payload.monitor_name }),
    device_type: payload.device_type ?? 'desktop',
    timeout: payload.timeout ?? 30000,
    session_cookies: payload.session_cookies,
    login_first: payload.login_first,
  };
}

async function postPdpTestToPath(
  path: string,
  payload: TestPdpRequest
): Promise<SyntheticTestExecutionResponse> {
  try {
    return await fetchWithTimeout((signal) =>
      scrapperApi.post<SyntheticTestExecutionResponse>(
        path,
        buildPdpRequestBody(payload),
        undefined,
        { signal }
      )
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    const isAbort = error instanceof Error && error.name === 'AbortError';
    throw new ApiError(
      isAbort
        ? 'Request timed out. Try again or ask the server admin to increase the gateway timeout.'
        : error instanceof Error
          ? error.message
          : 'Failed to run synthetic PDP test'
    );
  }
}

/** Run the given PDP test variant (each UI checkbox maps to one variant). */
export async function runPdpTestByVariant(
  variant: PdpTestVariant,
  payload: TestPdpRequest
): Promise<SyntheticTestExecutionResponse> {
  return postPdpTestToPath(PDP_VARIANT_TO_PATH[variant], payload);
}

/** Run PDP basic content test. Alias for runPdpTestByVariant('basic_content', payload). */
export async function testPdp(payload: TestPdpRequest): Promise<SyntheticTestExecutionResponse> {
  return runPdpTestByVariant('basic_content', payload);
}

// ---------------------------------------------------------------------------
// Cart test execution (view products, update quantity, remove product, proceed checkout)
// ---------------------------------------------------------------------------

function buildCartRequestBody(payload: TestCartRequest): Record<string, unknown> {
  return {
    page_url: payload.page_url,
    ...(payload.monitor_name != null && payload.monitor_name !== '' && { monitor_name: payload.monitor_name }),
    device_type: payload.device_type ?? 'desktop',
    timeout: payload.timeout ?? 30000,
  };
}

async function postCartTestToPath(
  path: string,
  payload: TestCartRequest
): Promise<SyntheticTestExecutionResponse> {
  try {
    return await fetchWithTimeout((signal) =>
      scrapperApi.post<SyntheticTestExecutionResponse>(
        path,
        buildCartRequestBody(payload),
        undefined,
        { signal }
      )
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    const isAbort = error instanceof Error && error.name === 'AbortError';
    throw new ApiError(
      isAbort
        ? 'Request timed out. Try again or ask the server admin to increase the gateway timeout.'
        : error instanceof Error
          ? error.message
          : 'Failed to run synthetic cart test'
    );
  }
}

/** Run the given cart test variant (each UI checkbox maps to one variant). */
export async function runCartTestByVariant(
  variant: CartTestVariant,
  payload: TestCartRequest
): Promise<SyntheticTestExecutionResponse> {
  return postCartTestToPath(CART_VARIANT_TO_PATH[variant], payload);
}

/** Run cart view products test. Alias for runCartTestByVariant('view_products', payload). */
export async function testCart(payload: TestCartRequest): Promise<SyntheticTestExecutionResponse> {
  return runCartTestByVariant('view_products', payload);
}

// ---------------------------------------------------------------------------
// Checkout test execution (access, shipping address, order summary, payment method, place order, order confirmation)
// ---------------------------------------------------------------------------

function buildCheckoutRequestBody(payload: TestCheckoutRequest): Record<string, unknown> {
  return {
    checkout_url: payload.checkout_url,
    ...(payload.monitor_name != null && payload.monitor_name !== '' && { monitor_name: payload.monitor_name }),
    device_type: payload.device_type ?? 'desktop',
    timeout: payload.timeout ?? 30000,
    session_cookies: payload.session_cookies ?? [],
  };
}

async function postCheckoutTestToPath(
  path: string,
  payload: TestCheckoutRequest
): Promise<SyntheticTestExecutionResponse> {
  try {
    return await fetchWithTimeout((signal) =>
      scrapperApi.post<SyntheticTestExecutionResponse>(
        path,
        buildCheckoutRequestBody(payload),
        undefined,
        { signal }
      )
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    const isAbort = error instanceof Error && error.name === 'AbortError';
    throw new ApiError(
      isAbort
        ? 'Request timed out. Try again or ask the server admin to increase the gateway timeout.'
        : error instanceof Error
          ? error.message
          : 'Failed to run synthetic checkout test'
    );
  }
}

/** Run the given checkout test variant (each UI checkbox maps to one variant). */
export async function runCheckoutTestByVariant(
  variant: CheckoutTestVariant,
  payload: TestCheckoutRequest
): Promise<SyntheticTestExecutionResponse> {
  return postCheckoutTestToPath(CHECKOUT_VARIANT_TO_PATH[variant], payload);
}

/** Run checkout access test. Alias for runCheckoutTestByVariant('access', payload). */
export async function testCheckout(payload: TestCheckoutRequest): Promise<SyntheticTestExecutionResponse> {
  return runCheckoutTestByVariant('access', payload);
}
